import { CALCULATE_SYSTEM_IMMEDIATE, CALCULATION_PROCESS_START, clearCalculationStatus } from 'actions/designer'
import { showNotification } from 'react-admin'
import { call, put } from 'redux-saga/effects'
import { CalculateSystemAction, CalculationRequestAction } from './calculations/types'
import {
  handleCalculationError,
  handleCalculationResult,
  logCalculationMetrics,
  prepareSystemForCalculation,
  prepareSystemShading,
  requestSystemCalculation,
  updateCalculationProcessStatus,
  updateModuleGridUI,
  validateAndGetSystemUuid,
  validateSystemAvailability,
} from './calculations/utils'

/**
 * Handles initial calculation request
 */
export function* handleCalculationRequest({ payload }: CalculationRequestAction) {
  const system = window.editor.objectByUuid(payload.uuid)
  if (system) {
    try {
      yield put({
        type: CALCULATION_PROCESS_START,
        payload: {
          uuid: payload.uuid,
          forceShadingCalculation: payload.forceShadingCalculation,
        },
      })

      yield put({
        type: CALCULATE_SYSTEM_IMMEDIATE,
        payload: {
          ...payload,
        },
      })
    } catch (e: any) {
      const detail = e.message
      yield put(
        showNotification(window.translate('System calculations could not be applied') + ': ' + detail, 'warning')
      )
      const callback = payload.callback
      if (callback) yield call(() => callback(false))
    }
  } else {
    yield put(showNotification('System no longer available', 'error'))
  }
}

/**
 * Core calculation wrapper
 * This function manages the entire calculation process
 * from preparing the system to handling results and errors.
 * It also manages the UI state and notifications.
 */
export function* handleCalculateSystem({ payload }: CalculateSystemAction) {
  let { uuid, callback, full_calcs, showSuccessNotification, forceShadingCalculation } = payload || {}
  const editor = window.editor
  let system_uuid = uuid

  try {
    // Validate system existence and state
    system_uuid = validateAndGetSystemUuid(editor, system_uuid)
    const system = validateSystemAvailability(editor, system_uuid)

    prepareSystemForCalculation(editor, system)

    yield call(prepareSystemShading, system, system_uuid, forceShadingCalculation)

    updateModuleGridUI(editor)

    const calculationStartTime = new Date()
    const calculationResult = yield call(performCalculation, system_uuid, full_calcs)

    const { response } = calculationResult

    logCalculationMetrics(system, calculationStartTime, full_calcs)

    yield call(handleCalculationResult, response, {
      system_uuid,
      system,
      callback,
      showSuccessNotification,
    })
  } catch (e: any) {
    yield call(handleCalculationError, e, callback, payload.uuid)
  } finally {
    yield put(clearCalculationStatus(payload.uuid))
  }
}

export function* performCalculation(system_uuid: string, full_calcs: boolean = false) {
  yield put(updateCalculationProcessStatus(system_uuid, 'waitingCalculation'))
  const response = yield call(() => requestSystemCalculation(system_uuid, full_calcs))
  return { response }
}
